import {useTranslation} from '@/translations/i18n.tsx'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams.ts'
import {AssignmentsURLParams} from '@features/home/types.ts'
import {useHomeAssignments} from '@features/home/queries/useHomeAssignments.ts'
import {ErrorBox} from '@components/commons/error-box'
import {AlertTriangleIcon, ArrowRightIcon, CalendarCheck02Icon} from '@components/ui/icon'
import {DataTable} from '@components/commons/data-table'
import {TableBodyCellText} from '@components/ui/table-atoms'
import {ListingTableCellInfo} from '@features/home/components/listing-table-cell-info'
import {generatePath, useLocation} from 'react-router-dom'
import {routes} from '@utilities/constants/routes.tsx'
import {HomeTableEmptyState} from '@features/home/components/home-table-empty-state'
import {HomeTableEmptySearch} from '@features/home/components/home-table-empty-search'
import {MobileTable} from '@components/commons/mobile-table.tsx'
import {TABLE_ELEMENT_LIMIT} from '@features/home/utils.tsx'
import {TableAreaItem, TableDateItems} from '@features/home/components/home-atoms'
import {ButtonLink} from '@components/commons/button-link'
import {useMe} from '@features/user/queries/useMe.ts'
import {useMediaQuery} from '@hooks/use-media-query'

export const UpcomingTable = ({onClearSearch}: {onClearSearch: () => void}) => {
    const {t} = useTranslation()
    const {search} = useLocation()
    const {searchParams, setSearchParams} = useParsedSearchParams(AssignmentsURLParams)
    const assignmentsQuery = useHomeAssignments(searchParams)
    const isLarge = useMediaQuery({breakpoint: 'md'})
    const meQuery = useMe({throwOnError: true})

    return (
        <div className="flex flex-col gap-4 items-stretch">
            <div className="flex w-full justify-between gap-2 flex-col md:flex-row">
                <p>{t('home:table:titles:assignments_have_been_scheduled')}</p>
                {meQuery.data?.calendar?.isConnectedToGoogle && (
                    <div className="flex items-center gap-1">
                        <CalendarCheck02Icon size={20} className="text-success-600" />
                        <p>{t('home:calendar_syncing')}</p>
                    </div>
                )}
            </div>
            {assignmentsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : assignmentsQuery.data?.data.length == 0 && !searchParams.search ? (
                <HomeTableEmptyState />
            ) : !isLarge ? (
                <MobileTable
                    pagination={{
                        ...assignmentsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: TABLE_ELEMENT_LIMIT,
                        onChangePage: page => setSearchParams({page})
                    }}
                    emptyStateComponent={<HomeTableEmptySearch onClearSearch={onClearSearch} />}
                    isLoading={assignmentsQuery.isFetching}
                    data={assignmentsQuery.data?.data ?? []}
                    rows={[
                        {
                            id: 'listing',
                            cell: ({dataItem}) => (
                                <div className="flex w-full justify-between">
                                    <ListingTableCellInfo item={dataItem} />
                                    <ButtonLink
                                        to={generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                            code: dataItem.code.toString()
                                        })}
                                        size={'sm'}
                                        variant={'tertiary'}
                                    >
                                        <ArrowRightIcon />
                                    </ButtonLink>
                                </div>
                            ),
                            header: () => <></>
                        },
                        {
                            cell: ({dataItem}) => <TableAreaItem assignment={dataItem} />,
                            header: () => <TableBodyCellText variant={'normal'}>{t('commons:area')}</TableBodyCellText>,
                            id: 'area'
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText variant={'dark'}>{dataItem.host.first_name}</TableBodyCellText>
                            ),
                            header: () => (
                                <TableBodyCellText variant={'normal'}>
                                    {t('dashboard:calendar:table:host')}
                                </TableBodyCellText>
                            ),
                            id: 'host'
                        },
                        {
                            cell: ({dataItem}) =>
                                dataItem.to_shoot_at ? <TableDateItems date={dataItem.to_shoot_at} /> : '-',
                            header: () => (
                                <TableBodyCellText variant={'normal'}>
                                    {t('home:table:scheduled_for')}
                                </TableBodyCellText>
                            ),
                            id: 'scheduled_for'
                        }
                    ]}
                />
            ) : (
                <DataTable
                    emptyComponent={<HomeTableEmptySearch onClearSearch={onClearSearch} />}
                    pagination={{
                        ...assignmentsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: TABLE_ELEMENT_LIMIT,
                        onChangePage: page => setSearchParams({page})
                    }}
                    isLoading={assignmentsQuery.isFetching}
                    data={assignmentsQuery.data?.data ?? []}
                    columnPinning={{right: ['cta'], left: ['listing']}}
                    order={{
                        orderBy: assignmentsQuery.sort.sortName ?? undefined,
                        orderDirection: assignmentsQuery.sort.direction ?? undefined
                    }}
                    setOrder={nextOrder =>
                        assignmentsQuery.sort.onSort(nextOrder?.orderBy ?? null, nextOrder?.orderDirection ?? null)
                    }
                    columns={[
                        {
                            cell: ({dataItem}) => <ListingTableCellInfo item={dataItem} />,
                            header: t('commons:listing').toUpperCase(),
                            id: 'listing',
                            size: 300,
                            sortKey: 'title'
                        },
                        {
                            cell: ({dataItem}) => <TableAreaItem assignment={dataItem} />,
                            header: t('commons:area').toUpperCase(),
                            id: 'area',
                            size: 300,
                            sortKey: 'address'
                        },
                        {
                            cell: ({dataItem}) => (
                                <TableBodyCellText variant={'dark'}>{dataItem.host.first_name}</TableBodyCellText>
                            ),
                            header: t('dashboard:calendar:table:host').toUpperCase(),
                            id: 'host',
                            size: 240,
                            sortKey: 'first_name'
                        },
                        {
                            cell: ({dataItem}) =>
                                dataItem.to_shoot_at ? <TableDateItems date={dataItem.to_shoot_at} /> : '-',
                            header: t('home:table:scheduled_for').toUpperCase(),
                            id: 'scheduled_for',
                            size: 240,
                            sortKey: 'to_shoot_at'
                        },
                        {
                            cell: ({dataItem}) => (
                                <ButtonLink
                                    to={{
                                        pathname: generatePath(routes.ASSIGNMENT.handle.fullPath, {
                                            code: dataItem.code.toString()
                                        }),
                                        search: search
                                    }}
                                    size={'sm'}
                                    variant={'tertiary'}
                                >
                                    {t('commons:open')}
                                </ButtonLink>
                            ),
                            header: <></>,
                            id: 'cta',
                            size: 80
                        }
                    ]}
                />
            )}
        </div>
    )
}
